import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import {
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    CircularProgress,
    Collapse,
    Typography,
    Box,
    Chip
} from "@material-ui/core";
import {
    Send as SendIcon,
    EmojiObjects as AIIcon,
    Close as CloseIcon,
    Help as HelpIcon
} from "@material-ui/icons";
import useList, { IFilters } from "../index";
import {
    useNaturalLanguageSearch,
    NLSearchResponse
} from "customHooks/useNaturalLanguageSearch";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: "relative",
            width: "100%",
            padding: theme.spacing(1),
            backgroundColor:
                theme.palette.type === "dark"
                    ? "rgba(103, 58, 183, 0.08)"
                    : "rgba(103, 58, 183, 0.04)",
            borderRadius: theme.shape.borderRadius,
            border: `1px dashed ${theme.palette.primary.light}`
        },
        searchContainer: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1)
        },
        searchField: {
            flex: 1,
            "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.paper
            }
        },
        aiIcon: {
            color: theme.palette.primary.main,
            animation: "$pulse 2s infinite"
        },
        "@keyframes pulse": {
            "0%": { opacity: 1 },
            "50%": { opacity: 0.5 },
            "100%": { opacity: 1 }
        },
        aiLabel: {
            fontSize: "0.7rem",
            color: theme.palette.primary.main,
            fontWeight: 600,
            marginBottom: theme.spacing(0.5),
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5)
        },
        interpretationBox: {
            margin: theme.spacing(1),
            padding: theme.spacing(1.5),
            backgroundColor: theme.palette.action.hover,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`
        },
        confidenceChip: {
            marginLeft: theme.spacing(1)
        },
        helpText: {
            fontSize: "0.75rem",
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(0.5),
            marginLeft: theme.spacing(1)
        },
        errorText: {
            color: theme.palette.error.main,
            fontSize: "0.75rem",
            marginTop: theme.spacing(0.5),
            marginLeft: theme.spacing(1)
        },
        appliedFiltersTitle: {
            fontSize: "0.75rem",
            fontWeight: 600,
            marginTop: theme.spacing(1.5),
            marginBottom: theme.spacing(0.75)
        },
        appliedFiltersList: {
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacing(0.75)
        }
    })
);

interface IProps {
    placeholder?: string;
    showInterpretation?: boolean;
    showConfidence?: boolean;
    showHelp?: boolean;
    onSearchComplete?: (result: NLSearchResponse) => void;
}

const explicitFilterLabels: Compleo.IObject = {
    currentSalary: "Salário atual",
    monthlySalaryClaim: "Pretensão salarial"
};

const formatFilterName = (fieldName: string, t: any): string => {
    if (explicitFilterLabels[fieldName]) {
        return explicitFilterLabels[fieldName];
    }

    const translatedValue = t(
        `otherFilter_${fieldName}`,
        t(fieldName, fieldName)
    );

    return String(translatedValue || fieldName);
};

const formatUnknownKey = (key: string): string => {
    return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[._]/g, " ")
        .trim();
};

const formatPrimitiveValue = (value: unknown): string => {
    if (typeof value === "boolean") {
        return value ? "Sim" : "Não";
    }

    if (value === null || value === undefined || value === "") {
        return "Sem valor";
    }

    return String(value);
};

const formatListValue = (value: unknown): string => {
    const values = Array.isArray(value) ? value : [value];

    return values
        .map((item: any) => {
            if (item && typeof item === "object") {
                return (
                    item.label ||
                    item.postData?.label ||
                    item.value ||
                    item.key ||
                    formatReadableValue(item)
                );
            }

            return formatPrimitiveValue(item);
        })
        .filter(Boolean)
        .join(", ");
};

const formatRangeValue = (
    value: Compleo.IObject,
    fromKey: string,
    toKey: string,
    fromLabel: string,
    toLabel: string
): string => {
    const parts: string[] = [];

    if (value[fromKey] !== undefined) {
        parts.push(`${fromLabel} ${formatPrimitiveValue(value[fromKey])}`);
    }

    if (value[toKey] !== undefined) {
        parts.push(`${toLabel} ${formatPrimitiveValue(value[toKey])}`);
    }

    return parts.join(" ");
};

const formatReadableValue = (value: unknown): string => {
    if (Array.isArray(value)) {
        return value.map(formatReadableValue).join(", ");
    }

    if (value && typeof value === "object") {
        const valueObject = value as Compleo.IObject;

        if (valueObject.searchText) {
            return formatPrimitiveValue(valueObject.searchText);
        }

        if (
            valueObject.numberFrom !== undefined ||
            valueObject.numberTo !== undefined
        ) {
            return formatRangeValue(
                valueObject,
                "numberFrom",
                "numberTo",
                "De",
                "até"
            );
        }

        if (
            valueObject.dateFrom !== undefined ||
            valueObject.dateTo !== undefined
        ) {
            return formatRangeValue(
                valueObject,
                "dateFrom",
                "dateTo",
                "De",
                "até"
            );
        }

        if (valueObject.listData !== undefined) {
            return formatListValue(valueObject.listData);
        }

        if (valueObject.value !== undefined || valueObject.label !== undefined) {
            return formatPrimitiveValue(valueObject.label || valueObject.value);
        }

        return Object.entries(valueObject)
            .map(
                ([key, itemValue]) =>
                    `${formatUnknownKey(key)}: ${formatReadableValue(itemValue)}`
            )
            .join(", ");
    }

    return formatPrimitiveValue(value);
};

const getAppliedFilterItems = (
    filters: Compleo.IObject | undefined,
    t: any
) => {
    const filterItems: { key: string; label: string; value: string }[] = [];

    if (!filters) {
        return filterItems;
    }

    if (filters.mainSearch) {
        filterItems.push({
            key: "mainSearch",
            label: String(
                t("ELASTICLISTSETTINGS:mainSearchListFilters", "Busca geral")
            ),
            value: formatPrimitiveValue(filters.mainSearch)
        });
    }

    Object.entries(filters.customSearch || {}).forEach(
        ([fieldName, fieldValue]) => {
            const value = formatReadableValue(fieldValue);
            if (value) {
                filterItems.push({
                    key: `customSearch.${fieldName}`,
                    label: formatFilterName(fieldName, t),
                    value
                });
            }
        }
    );

    Object.entries(filters.buckets || {}).forEach(([fieldName, bucketData]) => {
        const bucketValues = Array.isArray((bucketData as any)?.values)
            ? (bucketData as any).values
            : [];
        const value = formatListValue(
            bucketValues.map((item: any) => ({
                label: item?.label || item?.postData?.label,
                value: item?.value
            }))
        );

        if (value) {
            filterItems.push({
                key: `buckets.${fieldName}`,
                label: formatFilterName(fieldName, t),
                value
            });
        }
    });

    return filterItems;
};

/**
 * Natural Language Search Input component for useList
 *
 * Allows users to type queries in natural language (e.g., "candidates between 30 and 35 years with React knowledge")
 * and converts them to structured filters using LLM.
 *
 * @example
 * ```tsx
 * // Inside a CompleoListProvider context:
 * <NaturalLanguageSearchInput
 *     placeholder="Descreva sua busca..."
 *     showInterpretation={true}
 *     showConfidence={true}
 * />
 * ```
 */
const NaturalLanguageSearchInput = (props: IProps) => {
    const {
        placeholder = "Descreva sua busca em linguagem natural...",
        showInterpretation = true,
        showConfidence = true,
        showHelp = true,
        onSearchComplete
    } = props;

    const classes = useStyles();
    const [listData, listDispatch] = useList();
    const { setFilters } = listDispatch;
    const { t, moduleName } = listData.tempInfo;
    const { aggregations } = listData.listReturnInfo;

    const [query, setQuery] = React.useState("");
    const [showResult, setShowResult] = React.useState(false);

    const handleSearchSuccess = React.useCallback(
        (result: NLSearchResponse) => {
            // Apply filters based on merge strategy
            // Backend now returns bucket filters in the correct format (filters.buckets)
            // and non-bucket filters in customSearch format
            setFilters(
                (prevFilters: IFilters): IFilters => {
                    const mergeStrategy = result.mergeStrategy;
                    const nlFilters = result.filters;

                    if (mergeStrategy === "replace") {
                        return {
                            ...prevFilters,
                            mainSearch: (nlFilters.mainSearch as string) || "",
                            customSearch: nlFilters.customSearch || {},
                            buckets: nlFilters.buckets || {},
                            sort: nlFilters.sort || prevFilters.sort,
                            filterUpdated: true,
                            pagination: {
                                ...prevFilters.pagination,
                                currentPage: 0,
                                fromValue: 0
                            }
                        };
                    } else {
                        return {
                            ...prevFilters,
                            mainSearch:
                                (nlFilters.mainSearch as string) ||
                                prevFilters.mainSearch,
                            customSearch: {
                                ...prevFilters.customSearch,
                                ...(nlFilters.customSearch || {})
                            },
                            buckets: {
                                ...prevFilters.buckets,
                                ...(nlFilters.buckets || {})
                            },
                            sort: nlFilters.sort || prevFilters.sort,
                            filterUpdated: true,
                            pagination: {
                                ...prevFilters.pagination,
                                currentPage: 0,
                                fromValue: 0
                            }
                        };
                    }
                }
            );
            setShowResult(true);

            if (onSearchComplete) {
                onSearchComplete(result);
            }
        },
        [setFilters, onSearchComplete]
    );

    const {
        search,
        reset,
        result,
        isLoading,
        error,
        interpretation,
        confidence
    } = useNaturalLanguageSearch({
        moduleName,
        currentAggregations: aggregations || undefined,
        onSuccess: handleSearchSuccess
    });

    const handleSubmit = async () => {
        if (query.trim()) {
            await search(query);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
    };

    const handleClear = () => {
        setQuery("");
        setShowResult(false);
        reset();
    };

    const getConfidenceColor = (
        conf: number
    ): "default" | "primary" | "secondary" => {
        if (conf >= 0.8) return "primary";
        if (conf >= 0.5) return "default";
        return "secondary";
    };

    const getConfidenceLabel = (conf: number): string => {
        if (conf >= 0.8)
            return String(
                t(
                    "ELASTICLISTSETTINGS:nlSearch_highConfidence",
                    "Alta confiança"
                )
            );
        if (conf >= 0.5)
            return String(
                t(
                    "ELASTICLISTSETTINGS:nlSearch_mediumConfidence",
                    "Confiança média"
                )
            );
        return String(
            t("ELASTICLISTSETTINGS:nlSearch_lowConfidence", "Baixa confiança")
        );
    };

    const appliedFilterItems = React.useMemo(
        () => getAppliedFilterItems(result?.filters, t),
        [result?.filters, t]
    );

    return (
        <Box className={classes.root}>
            <Typography className={classes.aiLabel}>
                <AIIcon style={{ fontSize: 14 }} />
                {String(
                    t(
                        "ELASTICLISTSETTINGS:nlSearch_label",
                        "Busca Inteligente com IA"
                    )
                )}
            </Typography>
            <Box className={classes.searchContainer}>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Tooltip
                                    title={String(
                                        t(
                                            "ELASTICLISTSETTINGS:nlSearch_tooltip",
                                            "Busca inteligente com IA"
                                        )
                                    )}
                                >
                                    <AIIcon className={classes.aiIcon} />
                                </Tooltip>
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {query && (
                                    <IconButton
                                        size="small"
                                        onClick={handleClear}
                                        disabled={isLoading}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton
                                    size="small"
                                    onClick={handleSubmit}
                                    disabled={isLoading || !query.trim()}
                                    color="primary"
                                >
                                    {isLoading ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <SendIcon />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                {showHelp && (
                    <Tooltip
                        title={
                            <React.Fragment>
                                <Typography variant="subtitle2">
                                    {String(
                                        t(
                                            "ELASTICLISTSETTINGS:nlSearch_helpTitle",
                                            "Exemplos de busca:"
                                        )
                                    )}
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    <li>
                                        {String(
                                            t(
                                                "ELASTICLISTSETTINGS:nlSearch_example1",
                                                "candidatos entre 30 e 35 anos com conhecimento em React"
                                            )
                                        )}
                                    </li>
                                    <li>
                                        {String(
                                            t(
                                                "ELASTICLISTSETTINGS:nlSearch_example2",
                                                "vagas abertas nos últimos 7 dias"
                                            )
                                        )}
                                    </li>
                                    <li>
                                        {String(
                                            t(
                                                "ELASTICLISTSETTINGS:nlSearch_example3",
                                                "pessoas com pretensão salarial até 10000"
                                            )
                                        )}
                                    </li>
                                </ul>
                            </React.Fragment>
                        }
                    >
                        <IconButton size="small">
                            <HelpIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {error && (
                <Typography className={classes.errorText}>{error}</Typography>
            )}

            <Collapse in={showResult && showInterpretation && !!interpretation}>
                <Box className={classes.interpretationBox}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography variant="body2">
                            <strong>
                                {String(
                                    t(
                                        "ELASTICLISTSETTINGS:nlSearch_interpretation",
                                        "Interpretação"
                                    )
                                )}
                                :
                            </strong>{" "}
                            {interpretation}
                        </Typography>
                        {showConfidence && confidence > 0 && (
                            <Chip
                                size="small"
                                label={`${Math.round(
                                    confidence * 100
                                )}% - ${getConfidenceLabel(confidence)}`}
                                color={getConfidenceColor(confidence)}
                                className={classes.confidenceChip}
                            />
                        )}
                    </Box>
                    {appliedFilterItems.length > 0 && (
                        <Box>
                            <Typography className={classes.appliedFiltersTitle}>
                                {String(
                                    t(
                                        "ELASTICLISTSETTINGS:nlSearch_appliedFilters",
                                        "Filtros aplicados"
                                    )
                                )}
                            </Typography>
                            <Box className={classes.appliedFiltersList}>
                                {appliedFilterItems.map((item) => (
                                    <Chip
                                        key={item.key}
                                        size="small"
                                        variant="outlined"
                                        label={`${item.label}: ${item.value}`}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default NaturalLanguageSearchInput;
